import React, { useCallback, useState, useEffect } from 'react'
import { WithdrawalWayForm, Branch, WithdrawWayType } from '@golden/gdk-admin'
import Box from '@material-ui/core/Box'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import EditFinanceWithdrawalWayForm, { EditFinanceWithdrawalWayFormType, initialForm } from '../../../components/admin/finance/EditFinanceWithdrawalWayForm'
import useT from '../../../i18ns/admin/useT'
import { useDialogHandleSubmit, useRedirectHandleBack, useGetDataByParams, useRequestFromSearch } from '../../../utils/default/ComplexFlowHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import useGDK from '../../../providers/admin/gdk/useGDK'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { formatMoney } from '../../../utils/default/TableHelper'
import allRoute, { Path } from '../../../components/admin/route/route'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { searchToRequest } from './FinanceWithdrawalWayPage'
import useGDKStore from '../../../providers/admin/gdk/useGDKStore'

const formToRequest: (form: EditFinanceWithdrawalWayFormType) => WithdrawalWayForm = (form) => {
  return {
    forwarder_id: Number(form.courier),
    cash_min: form.min,
    cash_max: form.max,
    layer_ids: form.layer.layers,
    is_active: form.isActive,
    method: form.method as WithdrawWayType
  }
}

const FinanceWithdrawalWayUpdatePage: React.FC = () => {
  const request = useRequestFromSearch({ searchToRequest })
  const classes = useCommonStyles()
  const globalDialog = useGlobalDialog()
  const pageFlow = usePageFlow()
  const [id, setId] = useState<number>(0)
  const [defaultForm, setDefaultForm] = useState<EditFinanceWithdrawalWayFormType>(initialForm())
  const gdk = useGDK()
  const { t } = useT()
  const forwarders = useGDKStore.finance.forwarders()

  useGetDataByParams({
    path: Path.FINANCE_WITHDRAWAL_WAY_UPDATE,
    gdkFunc: (param: { id: string }) => gdk.finance.getWithdrawalWay(Number(param.id)),
    gdkFuncDependencies: [gdk],
    onBeforeFetch: pageFlow.setLoadingStart,
    onSuccess: (res) => {
      setId(res.id)
      setDefaultForm(initialForm({
        slug: res.slug,
        courierDropDown: `${res.forwarder_id}-${res.forwarder_name}`,
        courier: res.forwarder_id,
        name: res.forwarder_name,
        min: formatMoney(res.cash_min),
        max: formatMoney(res.cash_max),
        layer: {
          layers: res.layers.map((item) => item.id)
        },
        isActive: res.is_active,
        method: res.method
      }))
      pageFlow.setContentShow()
    },
    onError: pageFlow.setGDKError,
    canLoadData: id === 0
  })

  const [handleBack, handleDebouncedBack] = useRedirectHandleBack({
    path: allRoute.financeWithdrawalWay.encodePath({ search: { ...request }, param: {} })
  })

  const { handleSubmit } = useDialogHandleSubmit<EditFinanceWithdrawalWayFormType, WithdrawalWayForm, null>({
    dialogId: 'updateWay',
    globalDialog,
    getChangeDialogConfig: useCallback((form) => createGlobalDialogConfig({
      showIcon: false,
      message: (
        <React.Fragment>
          {t('dialog.confirmUpdateWithdrawal')}
          <span className={classes.purpleWord}>
            {`${forwarders[form.slug]?.name ?? ''} ${form.name}`}
          </span>
        </React.Fragment>
      )
    }), [t, forwarders, classes.purpleWord]),
    getSuccessDialogConfig: useCallback(() => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.updateWithdrawalSuccess')
    }), [t]),
    getFailDialogConfig: useCallback((error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error
    }), []),
    formToRequest,
    gdkFunc: (payload) => gdk.finance.updateWithdrawalWay(id, payload),
    gdkFuncDependencies: [gdk, id],
    afterSuccessDialog: handleBack
  })
  useEffect(() => {
    gdk.trunk.trigger([Branch.PLAYER_LAYER, Branch.FORWARDERS])
  }, [gdk])

  return (
    <Box padding={5}>
      <LoadingAndErrorFrame { ...pageFlow.status } >
        <EditFinanceWithdrawalWayForm
          title={t('common.updateWithdrawal')}
          okText={t('common.confirmUpdate')}
          onBack={handleDebouncedBack}
          onSubmit={handleSubmit}
          defaultValue={defaultForm}
          disableForwarder
        />
      </LoadingAndErrorFrame>
    </Box>
  )
}

export default React.memo(FinanceWithdrawalWayUpdatePage)
